import { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { Send, Save } from "lucide-react";
import { toast } from "sonner";

export default function TelegramPosterCard() {
  const [cfg, setCfg] = useState(null);
  const [token, setToken] = useState("");
  const [chatIds, setChatIds] = useState("");
  const [interval, setIntervalH] = useState(6);
  const [enabled, setEnabled] = useState(false);
  const [busy, setBusy] = useState(false);

  const load = async () => {
    try {
      const { data } = await api.get("/telegram/config");
      setCfg(data);
      setChatIds(data.chat_ids);
      setIntervalH(data.interval_hours);
      setEnabled(data.enabled);
    } catch { setCfg({}); }
  };
  useEffect(() => { load(); }, []);

  const save = async () => {
    setBusy(true);
    try {
      const body = { chat_ids: chatIds, interval_hours: Number(interval) || 6, enabled };
      if (token.trim()) body.bot_token = token.trim();
      await api.put("/telegram/config", body);
      setToken("");
      toast.success("Telegram poster settings saved");
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Could not save settings");
    } finally { setBusy(false); }
  };

  const postNow = async () => {
    setBusy(true);
    try {
      const { data } = await api.post("/telegram/post-now");
      toast.success(`Posted ad ${data.ad_code} to ${data.sent} chat${data.sent === 1 ? "" : "s"}`);
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Post failed");
    } finally { setBusy(false); }
  };

  if (!cfg) return null;

  return (
    <div className="mt-10 rounded-xl border border-[#27272A] bg-[#121212] p-6" data-testid="telegram-poster-card">
      <div className="flex items-center gap-2">
        <Send className="h-4 w-4 text-[#2AABEE]" />
        <h2 className="font-black text-xl tracking-tight">Telegram auto-poster</h2>
        <span className={`text-[10px] uppercase tracking-widest px-2 py-0.5 rounded-full border ${enabled ? "text-[#34C759] border-[#34C759]/40 bg-[#34C759]/10" : "text-neutral-500 border-white/10 bg-white/5"}`} data-testid="telegram-status-badge">
          {enabled ? "on" : "off"}
        </span>
      </div>
      <p className="text-xs text-neutral-500 mt-1">
        Rotates your active adverts into Telegram channels/groups. Create a bot with <span className="font-mono text-neutral-400">@BotFather</span>, add it as admin to your channel, paste the token below.
      </p>

      <div className="mt-4 grid md:grid-cols-2 gap-3">
        <input type="password" value={token} onChange={(e) => setToken(e.target.value)}
          placeholder={cfg.token_set ? "Bot token saved ✓ — paste to replace" : "Bot token from @BotFather"}
          data-testid="telegram-token-input"
          className="h-10 rounded-md bg-[#0A0A0A] border border-[#27272A] px-3 text-sm text-white font-mono focus:outline-none focus:border-[#2AABEE]" />
        <input value={chatIds} onChange={(e) => setChatIds(e.target.value)}
          placeholder="@channelname, -1001234567890 (comma-separated)"
          data-testid="telegram-chats-input"
          className="h-10 rounded-md bg-[#0A0A0A] border border-[#27272A] px-3 text-sm text-white font-mono focus:outline-none focus:border-[#2AABEE]" />
        <div className="flex items-center gap-3">
          <label className="text-xs text-neutral-400">Post every</label>
          <input type="number" min={1} max={168} value={interval} onChange={(e) => setIntervalH(e.target.value)}
            data-testid="telegram-interval-input"
            className="h-10 w-20 rounded-md bg-[#0A0A0A] border border-[#27272A] px-3 text-sm text-white font-mono focus:outline-none focus:border-[#2AABEE]" />
          <span className="text-xs text-neutral-400">hours</span>
          <label className="ml-4 flex items-center gap-2 text-xs text-neutral-400 cursor-pointer">
            <input type="checkbox" checked={enabled} onChange={(e) => setEnabled(e.target.checked)} data-testid="telegram-enabled-toggle" className="accent-[#2AABEE]" />
            Auto-posting enabled
          </label>
        </div>
        <div className="flex items-center gap-2 justify-end">
          {cfg.token_set && (
            <button onClick={async () => { try { await api.put("/telegram/config", { clear_token: true }); toast.success("Bot token removed"); load(); } catch { toast.error("Could not remove token"); } }}
              data-testid="telegram-clear-token-btn"
              className="h-10 px-3 rounded-full border border-[#27272A] text-xs text-neutral-500 hover:border-[#FF3B30] hover:text-[#FF3B30] transition-colors">
              Remove token
            </button>
          )}
          <button onClick={save} disabled={busy} data-testid="telegram-save-btn"
            className="h-10 px-4 inline-flex items-center gap-2 rounded-full border border-[#27272A] text-sm font-bold text-white hover:border-[#2AABEE] transition-colors disabled:opacity-50">
            <Save className="h-4 w-4" /> Save
          </button>
          <button onClick={postNow} disabled={busy || !cfg.token_set} data-testid="telegram-post-now-btn"
            title={cfg.token_set ? "Post the next active advert now" : "Save a bot token first"}
            className={`h-10 px-4 inline-flex items-center gap-2 rounded-full text-sm font-bold transition-colors ${cfg.token_set && !busy ? "bg-[#2AABEE] text-white hover:bg-[#2497d3]" : "bg-white/5 text-neutral-600 border border-[#27272A] cursor-not-allowed"}`}>
            <Send className="h-4 w-4" /> Post now
          </button>
        </div>
      </div>

      {(cfg.last_post_at || cfg.last_error) && (
        <div className="mt-3 text-xs font-mono" data-testid="telegram-last-status">
          {cfg.last_post_at && <span className="text-neutral-500">Last post: {new Date(cfg.last_post_at).toLocaleString()} (ad {cfg.last_posted_code})</span>}
          {cfg.last_error && <span className="text-[#FF3B30] ml-3">Error: {cfg.last_error}</span>}
        </div>
      )}
    </div>
  );
}
